import { useCallback, useEffect, useRef, useState } from 'react';

import { commitOwnerKey, generateOwnerKey, wrapBridgeKey } from '@/crypto';
import { transport } from '@/api/transport';
import { ApiError } from '@/api/types';
import type { CodeState, ConnectRequest, PendingDevice, ShareTab } from '@/components/ShareOverlay.types';
import { useToast } from '@/components/Toast';
import { t } from '@/i18n';
import { sasEmoji } from '@/lib/nearby';

interface Options {
  readonly bridgeId: string;
  readonly bridgeKey: string;
  readonly ownerToken: string;
  readonly tab: ShareTab;
  readonly connectRequest: ConnectRequest | null;
  readonly onDeviceConnected: () => void;
}

/** The owner's side of pairing by a connect code: a live code, the device that typed it and the owner's yes or no. */
export function useConnectCode({ bridgeId, bridgeKey, ownerToken, tab, connectRequest, onDeviceConnected }: Options) {
  const toast = useToast();
  const [code, setCode] = useState<CodeState | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [pending, setPending] = useState<PendingDevice | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const handled = useRef<string | null>(null);

  const startCode = useCallback(async () => {
    setCodeError(null);
    try {
      const pair = await generateOwnerKey();
      const created = await transport.createConnectCode({ bridgeId, ownerToken, ownerKeyHash: await commitOwnerKey('connect', pair.publicKey) });
      setCode({ sessionId: created.sessionId, code: created.code, expiresAt: new Date(created.expiresAt).getTime(), pair });
    } catch (error) {
      setCode(null);
      setCodeError(error instanceof ApiError && error.code === 'view_limit' ? t('share.noViewsLeft') : t('useConnectCode.startFailed'));
    }
  }, [bridgeId, ownerToken]);

  // The code tab keeps a live code: start one when opened, renew when it expires.
  const needsCode = tab === 'code' && !connected && pending === null && code === null && codeError === null;
  useEffect(() => {
    if (!needsCode) return undefined;
    const handle = window.setTimeout(() => void startCode(), 0);
    return () => {
      window.clearTimeout(handle);
    };
  }, [needsCode, startCode]);

  useEffect(() => {
    if (tab !== 'code' || connected) return undefined;
    const timer = window.setInterval(() => {
      const current = Date.now();
      setNow(current);
      setCode((active) => (active !== null && current >= active.expiresAt ? null : active));
    }, 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, [tab, connected]);

  // A device typed our code. Only now does the owner show its key (the code carried a commitment to it, so a server
  // cannot pick a key to suit the emoji), and both screens then show four emoji from both keys. The owner says
  // whether they match before the key goes anywhere: a code could be guessed or overheard, and then the first one to
  // type it would otherwise receive the bridge.
  useEffect(() => {
    if (!connectRequest || connectRequest.sessionId !== code?.sessionId || handled.current === connectRequest.sessionId) return;
    handled.current = connectRequest.sessionId;
    const { sessionId, pair } = code;
    const run = async (): Promise<void> => {
      try {
        await transport.revealConnectKey({ bridgeId, ownerToken, sessionId, ownerPublicKey: pair.publicKey });
        const emoji = await sasEmoji('connect', pair.publicKey, connectRequest.guestPublicKey);
        setPending({ sessionId, guestPublicKey: connectRequest.guestPublicKey, device: connectRequest.device, sameNetwork: connectRequest.sameNetwork, emoji, pair });
      } catch {
        setCodeError(t('useConnectCode.checkFailed'));
        setCode(null);
      }
    };
    void run();
  }, [connectRequest, code, bridgeId, ownerToken]);

  const allow = async (): Promise<void> => {
    if (pending === null) return;
    try {
      const wrapped = await wrapBridgeKey(pending.pair, pending.guestPublicKey, bridgeKey, { flow: 'connect', id: pending.sessionId });
      await transport.completeConnect({ bridgeId, ownerToken, sessionId: pending.sessionId, wrappedKey: wrapped });
      setPending(null);
      setConnected(true);
      toast.notify(t('shareCode.connected'));
      onDeviceConnected();
    } catch {
      setPending(null);
      setCodeError(t('useConnectCode.handFailed'));
      setCode(null);
    }
  };

  const refuse = (): void => {
    setPending(null);
    setCode(null);
  };

  const secondsLeft = code ? Math.max(0, Math.ceil((code.expiresAt - now) / 1000)) : 0;
  const progress = code ? Math.min(100, (secondsLeft / 60) * 100) : 0;

  return { code, codeError, connected, pending, secondsLeft, progress, startCode, allow, refuse };
}
